import React, { useCallback, useMemo } from 'react';
import { TouchableHighlight, View } from 'react-native';
import { format } from 'date-fns';
import { rem } from 'design-system';
import Text from 'design-system/Text';
import Amount from 'design-system/Amount';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { GroupTransactionLogo } from './GroupTransactionLogo';
const GroupTransaction = ({ usersByIds, onPress, transaction, withWrapper = false, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const borderStyles = useMemo(() => [
        transaction.isFirstInSection && styles.firstItem,
        transaction.isLastInSection && styles.lastItem,
    ], [
        styles.firstItem,
        styles.lastItem,
        transaction.isFirstInSection,
        transaction.isLastInSection,
    ]);
    const handlePress = useCallback(() => {
        onPress?.(transaction);
    }, [onPress, transaction]);
    const payer = useMemo(() => usersByIds[Number(transaction.paidBy)], [transaction.paidBy, usersByIds]);
    const payee = useMemo(() => transaction.isSettlement
        ? usersByIds[Number(Object.keys(transaction.split)[0])]
        : undefined, [transaction.isSettlement, transaction.split, usersByIds]);
    const { firstBalance, firstCurrency, secondBalance, secondCurrency } = useMemo(() => {
        let firstBalance = transaction.amount;
        const firstCurrency = transaction.currency;
        let secondBalance = transaction.convertedAmount;
        const secondCurrency = transaction.convertedCurrency;
        if (transaction.isSettlement) {
            firstBalance = Math.abs(firstBalance);
            if (secondBalance) {
                secondBalance = Math.abs(secondBalance);
            }
        }
        return { firstBalance, firstCurrency, secondBalance, secondCurrency };
    }, [
        transaction.amount,
        transaction.convertedAmount,
        transaction.convertedCurrency,
        transaction.currency,
        transaction.isSettlement,
    ]);
    return (<View style={withWrapper ? [styles.wrapper, borderStyles] : null}>
      <TouchableHighlight activeOpacity={1} onPress={handlePress} style={withWrapper ? borderStyles : null} underlayColor={colors.background.underlay}>
        <View style={styles.itemContainer}>
          <View style={styles.logo}>
            <GroupTransactionLogo R44 transaction={transaction}/>
          </View>
          <View style={styles.flex}>
            <Text Text-16 numberOfLines={1}>
              {transaction.counterpartName}
            </Text>
            {transaction.isSettlement ? (<Text TextThin-14 Gray numberOfLines={2}>
                <Text Text-14 Secondary>
                  {payer}
                </Text>
                <Text TextThin-14 Gray>
                  {' '}
                  to{' '}
                </Text>
                <Text Text-14 Secondary>
                  {payee}
                </Text>
              </Text>) : (<Text TextThin-14 Gray numberOfLines={2}>
                {transaction.amount > 0 ? 'Received' : 'Paid'} by{' '}
                <Text Text-14 Secondary>
                  {payer}
                </Text>
              </Text>)}
          </View>
          <View style={styles.rightContent}>
            <Amount OffBlack Numbers-16 value={{ value: firstBalance, currency: firstCurrency }}/>
            {secondCurrency && secondBalance != null ? (<Amount Gray NumbersThin-14 value={{ value: secondBalance, currency: secondCurrency }} showSign/>) : null}
            <Text TextThin-14 Gray style={styles.date}>
              {format(new Date(transaction.date), 'd MMM')}
            </Text>
          </View>
        </View>
      </TouchableHighlight>
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    wrapper: {
        marginHorizontal: rem(16),
        backgroundColor: colors.cards.onDark,
    },
    firstItem: {
        borderTopLeftRadius: rem(16),
        borderTopRightRadius: rem(16),
    },
    lastItem: {
        borderBottomLeftRadius: rem(16),
        borderBottomRightRadius: rem(16),
    },
    date: {
        marginTop: rem(2),
    },
    itemContainer: {
        alignItems: 'center',
        flexDirection: 'row',
        paddingVertical: rem(16),
        paddingHorizontal: rem(16),
    },
    flex: {
        flex: 1,
    },
    row: {
        flexDirection: 'row',
    },
    rightContent: {
        alignItems: 'flex-end',
    },
    logo: {
        marginRight: rem(16),
    },
}));
export default React.memo(GroupTransaction);
